import { ReactNode, useEffect, useState } from "react";
import { skipToken } from "@reduxjs/toolkit/query";
import {
  Button,
  Card,
  CardBody,
  Collapse,
  Modal,
  ModalBody,
  Table,
} from "reactstrap";
import {
  Calendar,
  MapPin,
  Briefcase,
  CheckCircle,
  XCircle,
  Coffee,
  Sun,
  Gift,
  BarChart2,
  ChevronDown,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
} from "react-feather";
import useUser from "../../../../hooks/useUser";
import { useGetEmployeeDetailsQuery } from "../../../../ReduxToolkit/api/employee";
import { formatReportDate } from "../../../../utils/reportDateUtils";
import {
  missingAttendanceHours,
  missingAttendanceMinutes,
} from "../../../../utils/attendanceDuration";
import "../../dashboard.scss";
import EmployeeCardHeader from "../../../employeeManagement/components/EmployeeCardHeader";
import SearchableSelect from "../../../../CommonElements/SearchableSelect";
import { IProfileLeaveAttendancePopup } from "../../../../Types/UserProfileDetails";

const completePopup = () => { };

const AttendanceSummaryPopup = ({
  canShow,
  onComplete = completePopup,
  employeeId,
  embedded = false,
  summaryData,
  hideHeader = false,
  dailyAttendanceContent,
  renderDayAction,
  renderDayTime,
  initialPageSize = 6,
  canViewTeamAttendance = false,
}: {
  canShow: boolean;
  onComplete?: () => void;
  employeeId?: string | number;
  embedded?: boolean;
  summaryData?: IProfileLeaveAttendancePopup;
  hideHeader?: boolean;
  dailyAttendanceContent?: ReactNode;
  renderDayAction?: (date: string) => ReactNode;
  renderDayTime?: (
    date: string,
    kind: "in_time" | "out_time",
    time: string | null,
  ) => ReactNode;
  initialPageSize?: number;
  canViewTeamAttendance?: boolean;
}) => {
  const user = useUser();
  const role = user?.user_type
    ?.trim()
    .toLowerCase()
    .replace(/[\s_]+/g, "");
  const canViewSummary = true;
  // const canViewSummary =
  //   role === "admin" ||
  //   role === "hr" ||
  //   (embedded &&
  //     Boolean(summaryData) &&
  //     (String(employeeId ?? user?.id) === String(user?.id) ||
  //       (role === "manager" && canViewTeamAttendance)));
  const profileId = employeeId ?? user?.id;
  const { data, isSuccess, isError } = useGetEmployeeDetailsQuery(
    canViewSummary && profileId && !summaryData ? String(profileId) : skipToken,
  );
  const summary = summaryData ?? data?.data?.leave_attendance_popup;
  const [isOpen, setIsOpen] = useState(false);
  const [hasShown, setHasShown] = useState(false);
  const [showHours, setShowHours] = useState(false);
  const [status, setStatus] = useState("all");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);
  useEffect(() => {
    if (!embedded && canViewSummary && summary && canShow && !hasShown) {
      setIsOpen(true);
      setHasShown(true);
    }
  }, [embedded, canViewSummary, summary, canShow, hasShown]);
  useEffect(() => {
    if (
      !embedded &&
      canShow &&
      ((role && !canViewSummary) || isError || (isSuccess && !summary))
    )
      onComplete();
  }, [
    embedded,
    role,
    canViewSummary,
    canShow,
    isError,
    isSuccess,
    summary,
    onComplete,
  ]);
  if (!canViewSummary || !summary) return null;
  const attendance = summary.attendance_summary;
  const leaveTypes = Object.entries(
    attendance.leaves_by_type ?? attendance.leave_by_type ?? {},
  );
  const hours = summary.working_hours;

  const kpis = [
    {
      label: "Total Days",
      value: attendance.total_days,
      icon: Calendar,
      tone: "purple",
    },
    {
      label: "Working Days",
      value: attendance.working_days,
      icon: Briefcase,
      tone: "blue",
    },
    {
      label:
        attendance.comp_off && attendance.comp_off > 0
          ? "Sunday + Comp Off"
          : "Sundays",
      value: attendance.sunday + (attendance.comp_off ?? 0),
      icon: Sun,
      tone: "amber",
    },
    {
      label: "Holidays",
      value: attendance.holiday,
      icon: Gift,
      tone: "blue",
    },

    // {
    //   label: "Comp Off/Week Off",
    //   value: attendance.comp_off,
    //   icon: Calendar,
    //   tone: "green",
    // },
    {
      label: "Present",
      value: attendance.present,
      icon: CheckCircle,
      tone: "green",
    },
    {
      label: "Absent",
      value: attendance.absent,
      icon: XCircle,
      tone: "red",
    },
    {
      label: "Leave",
      value: attendance.leave,
      icon: Coffee,
      tone: "purple",
    },
  ].filter((item) => Number(item.value) > 0);

  const days = [...hours.daily_breakdown].sort((a, b) =>
    b.date.localeCompare(a.date),
  );
  const filtered =
    status === "all" ? days : days.filter((day) => day.status === status);
  const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));
  const currentPage = Math.min(page, pageCount);
  const offset = (currentPage - 1) * pageSize;
  const pageDays = filtered.slice(offset, offset + pageSize);
  const formatCycleDate = (value: string) => {
    const parts = formatReportDate(value).split("/");
    if (parts.length !== 3) return "-";
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    return `${parts[0]} ${months[Number(parts[1]) - 1]} ${parts[2]}`;
  };
  const statusTone = (value: string) =>
    /absent/i.test(value)
      ? "red"
      : /present/i.test(value)
        ? "green"
        : /leave/i.test(value)
          ? "purple"
          : "blue";
  const SummaryBody = embedded ? CardBody : ModalBody;
  const content = (
    <>
      {hideHeader ? null : embedded ? (
        <EmployeeCardHeader
          headClass="b-l-primary border-3"
          titleClass="f-w-400 f-14"
          title={`Attendance Summary (${formatCycleDate(summary.cycle_start)} – ${formatCycleDate(summary.cycle_end)})`}
          showEditInfo={false}
        />
      ) : (
        <div className="modal-header attendance-summary-header">
          <span className="attendance-summary-hero-icon">
            <Calendar size={20} aria-hidden="true" />
          </span>
          <div>
            <h5 id="attendance-summary-title" className="modal-title">
              Attendance Summary{" "}
              <span className="attendance-summary-period">
                ({formatCycleDate(summary.cycle_start)} –{" "}
                {formatCycleDate(summary.cycle_end)})
              </span>
            </h5>
          </div>
          {!embedded && (
            <button
              type="button"
              className="btn-close"
              aria-label="Close attendance summary"
              onClick={() => setIsOpen(false)}
            />
          )}
        </div>
      )}
      <SummaryBody className={embedded ? "p-3" : undefined}>
        <div className="attendance-summary-overview">
          <div className="attendance-summary-grid">
            {kpis.map(({ label, value, icon: Icon, tone }) => (
              <div
                className={`attendance-summary-kpi attendance-tone-${tone}`}
                key={label}
              >
                <span className="attendance-summary-icon">
                  <Icon size={18} aria-hidden="true" />
                </span>
                <strong className="attendance-summary-value">
                  {value ?? "-"}
                </strong>
                <div className="attendance-summary-label">{label}</div>
              </div>
            ))}
          </div>
          {leaveTypes.length > 0 && (
            <div className="attendance-leave-types" aria-label="Leave by type">
              <span className="attendance-leave-types-title">
                <Coffee size={14} aria-hidden="true" /> Leave by type
              </span>
              {leaveTypes.map(([type, count]) => (
                <span className="attendance-leave-type" key={type}>
                  <span>{type}</span>
                  <strong>{count}</strong>
                </span>
              ))}
            </div>
          )}
          <div className="attendance-hours-panel">
            <Button
              color="light"
              className="attendance-hours-toggle"
              onClick={() => setShowHours((open) => !open)}
              aria-expanded={showHours}
              aria-controls="attendance-working-hours"
            >
              <span>
                <BarChart2 size={16} aria-hidden="true" /> Working Hours
                (Monthly)
              </span>
              <ChevronDown
                size={16}
                aria-hidden="true"
                className={
                  showHours
                    ? "attendance-chevron expanded"
                    : "attendance-chevron"
                }
              />
            </Button>
            <Collapse isOpen={showHours}>
              <div
                id="attendance-working-hours"
                className="attendance-hours-grid"
              >
                {[
                  { label: "Daily Target", value: hours.standard_daily_hours },
                  {
                    label: "Required Hours Monthly",
                    value: hours.expected_hours,
                  },
                  { label: "Logged Hours Monthly", value: hours.logged_hours },
                  {
                    label: "Deficit Hours Monthly",
                    value: hours.deficit_hours,
                  },
                ]
                  .filter(({ value }) => value != null)
                  .map(({ label, value }) => (
                    <div key={label}>
                      <span>{label}</span>
                      <strong>{value ?? "-"} hrs</strong>
                    </div>
                  ))}
              </div>
            </Collapse>
          </div>
          <div
            className={`attendance-hours-notice ${hours.has_deficiency ? "has-deficit" : ""}`}
            role="status"
          >
            {hours.has_deficiency ? (
              <AlertCircle size={17} aria-hidden="true" />
            ) : (
              <CheckCircle size={17} aria-hidden="true" />
            )}
            <span>
              {hours.has_deficiency
                ? `${hours.deficit_hours} hours below the expected total for this cycle`
                : "You have met the expected working hours for this cycle"}
            </span>
          </div>
        </div>
        {dailyAttendanceContent ?? (
          <>
            <div className="attendance-daily-heading">
              <h6>Daily attendance</h6>
              <SearchableSelect
                className="attendance-status-select"
                name="attendance-status-filter"
                label="Filter attendance by status"
                value={status}
                searchable={false}
                showSelectedBadge={false}
                options={[
                  { value: "all", label: "All statuses" },
                  ...Array.from(new Set(days.map((day) => day.status))).map(
                    (value) => ({ value, label: value }),
                  ),
                ]}
                onChange={(value) => {
                  setStatus(String(value));
                  setPage(1);
                }}
              />
            </div>
            <div className="table-responsive attendance-summary-table d-none d-md-block">
              <Table size="sm" className="mb-0 align-middle">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>In time</th>
                    <th>Out time</th>
                    <th>Logged hours</th>
                    <th>Deficit hours</th>
                    <th>Status</th>
                    {renderDayAction && <th>Action</th>}
                  </tr>
                </thead>
                <tbody>
                  {pageDays.map((day) => (
                    <tr key={day.date}>
                      <td className="text-nowrap">
                        {formatReportDate(day.date)}
                      </td>
                      <td>
                        {renderDayTime
                          ? renderDayTime(day.date, "in_time", day.in_time)
                          : (day.in_time ?? "-")}
                      </td>
                      <td>
                        {renderDayTime
                          ? renderDayTime(day.date, "out_time", day.out_time)
                          : (day.out_time ?? "-")}
                      </td>
                      <td>{day.logged_hours} hrs</td>
                      <td
                        className={
                          (missingAttendanceMinutes(
                            day.expected_hours,
                            day.logged_hours,
                          ) ?? 0) > 0
                            ? "text-danger fw-semibold"
                            : ""
                        }
                      >
                        {missingAttendanceHours(
                          day.expected_hours,
                          day.logged_hours,
                        )}
                      </td>
                      <td>
                        <span
                          className={`attendance-day-status attendance-tone-${statusTone(day.status)}`}
                        >
                          {day.status}
                        </span>
                      </td>
                      {renderDayAction && <td>{renderDayAction(day.date)}</td>}
                    </tr>
                  ))}
                  {!pageDays.length && (
                    <tr>
                      <td
                        colSpan={renderDayAction ? 7 : 6}
                        className="text-center py-3"
                      >
                        No attendance records for this status.
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
            <div className="attendance-mobile-list d-md-none">
              {pageDays.map((day) => {
                const missing =
                  missingAttendanceMinutes(
                    day.expected_hours,
                    day.logged_hours,
                  ) ?? 0;
                return (
                  <article
                    className="attendance-mobile-card"
                    key={day.date}
                    aria-label={`Attendance for ${formatReportDate(day.date)}`}
                  >
                    <div className="attendance-mobile-card-header">
                      <strong>
                        <Calendar size={14} aria-hidden="true" />
                        {formatReportDate(day.date)}
                      </strong>
                      <span
                        className={`attendance-day-status attendance-tone-${statusTone(day.status)}`}
                      >
                        {day.status}
                      </span>
                      {renderDayAction && renderDayAction(day.date)}
                    </div>
                    <dl className="attendance-mobile-card-details">
                      <div>
                        <dt>In time</dt>
                        <dd>
                          {renderDayTime ? (
                            renderDayTime(day.date, "in_time", day.in_time)
                          ) : (
                            <span
                              className={`attendance-time-badge ${day.in_time ? "is-in" : "is-empty"}`}
                            >
                              {day.in_time ?? "–"}
                            </span>
                          )}
                        </dd>
                      </div>
                      <div>
                        <dt>Out time</dt>
                        <dd>
                          {renderDayTime ? (
                            renderDayTime(day.date, "out_time", day.out_time)
                          ) : (
                            <span
                              className={`attendance-time-badge ${day.out_time ? "is-out" : "is-empty"}`}
                            >
                              {day.out_time ?? "–"}
                            </span>
                          )}
                        </dd>
                      </div>
                      <div>
                        <dt>Logged hours</dt>
                        <dd>{day.logged_hours} hrs</dd>
                      </div>
                      <div>
                        <dt>Missing hours</dt>
                        <dd className={missing > 0 ? "text-danger" : undefined}>
                          {missingAttendanceHours(
                            day.expected_hours,
                            day.logged_hours,
                          )}
                        </dd>
                      </div>
                    </dl>
                    {(day.in_location || day.out_location) && (
                      <dl className="attendance-mobile-locations">
                        <div>
                          <dt>
                            <MapPin size={13} aria-hidden="true" />
                            In location
                          </dt>
                          <dd>{day.in_location || "–"}</dd>
                        </div>
                        <div>
                          <dt>
                            <MapPin size={13} aria-hidden="true" />
                            Out location
                          </dt>
                          <dd>{day.out_location || "–"}</dd>
                        </div>
                      </dl>
                    )}
                  </article>
                );
              })}
              {!pageDays.length && (
                <div className="text-center text-muted py-3">
                  No attendance records for this status.
                </div>
              )}
            </div>
            <div className="attendance-pagination">
              <div className="attendance-page-size">
                <span id="attendance-page-size-label">Rows per page</span>
                <SearchableSelect
                  name="attendance-page-size"
                  label="Rows per page"
                  className="attendance-page-size-select"
                  value={pageSize}
                  searchable={false}
                  showSelectedBadge={false}
                  menuPlacement="top"
                  options={[6, 10, 20, 50].map((size) => ({
                    value: size,
                    label: `${size} rows`,
                  }))}
                  onChange={(value) => {
                    setPageSize(Number(value));
                    setPage(1);
                  }}
                />
              </div>
              <span>
                Showing {filtered.length ? offset + 1 : 0}–
                {Math.min(offset + pageSize, filtered.length)} of{" "}
                {filtered.length} days
              </span>
              <div className="attendance-page-buttons">
                <button
                  disabled={currentPage === 1}
                  aria-label="Previous page"
                  onClick={() => setPage(currentPage - 1)}
                >
                  <ChevronLeft size={14} />
                </button>
                {Array.from({ length: pageCount }, (_, i) => i + 1)
                  .filter(
                    (value) =>
                      value === 1 ||
                      value === pageCount ||
                      Math.abs(value - currentPage) <= 1,
                  )
                  .map((value, index, pages) => (
                    <span key={value}>
                      {index > 0 && value - pages[index - 1] > 1 && (
                        <span className="attendance-page-gap">…</span>
                      )}
                      <button
                        className={value === currentPage ? "active" : ""}
                        aria-label={`Page ${value}`}
                        aria-current={
                          value === currentPage ? "page" : undefined
                        }
                        onClick={() => setPage(value)}
                      >
                        {value}
                      </button>
                    </span>
                  ))}
                <button
                  disabled={currentPage === pageCount}
                  aria-label="Next page"
                  onClick={() => setPage(currentPage + 1)}
                >
                  <ChevronRight size={14} />
                </button>
              </div>
            </div>
          </>
        )}
      </SummaryBody>
    </>
  );
  if (embedded && hideHeader) return <>{content}</>;
  if (embedded)
    return (
      <Card
        className="attendance-summary-popup attendance-summary-profile"
        aria-label="Attendance Summary"
      >
        {content}
      </Card>
    );
  return (
    <Modal
      isOpen={isOpen && canShow}
      toggle={() => setIsOpen(false)}
      onClosed={onComplete}
      centered
      scrollable
      className="attendance-summary-dialog"
      labelledBy="attendance-summary-title"
      contentClassName="attendance-summary-popup"
    >
      {content}
    </Modal>
  );
};
export default AttendanceSummaryPopup;
